import React, { useState, useEffect, useLayoutEffect } from "react";
import ReactMarkdown from "react-markdown";
import "./MainContent.css";
import { chatApi, sendFeedback } from "../api";
import ChatBox from "./ChatBox";
import ResponseTabs from "./ResponseTabs";
import { AiOutlineLike, AiFillLike, AiOutlineDislike, AiFillDislike } from "react-icons/ai";

// Removed custom UI imports, will use simple modal/tabs below
// import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from "recharts";
// import { Copy } from "lucide-react";

// Simple HTML escape to prevent XSS from localStorage
function escapeHTML(str) {
  if (typeof str !== 'string') return str;
  return str.replace(/[&<>'"]/g, function (tag) {
    const charsToReplace = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    };
    return charsToReplace[tag] || tag;
  });
}

const FNO_SUGGESTIONS = "Please give us LOB wise trend of Firm and Opportunity break up for current quarter?|What is the total value of Risk against DFS revenue in Delivery?|Please give us Top 10 customers, contributing to revenue growth?";
const fnoSuggestions = (process.env.REACT_APP_FNO_SUGGESTIONS || FNO_SUGGESTIONS)
  .split("|")
  .filter(q => q.trim());

const dashboardSuggestions = {
  "Firm and Opportunity": fnoSuggestions,
  "Order Book": []
};


function MainContent({ activeDashboard, selectedQuery, clearChatTrigger, user }) {
  // Dummy API response state
  const [response, setResponse] = useState({ textData: "", tableData: {}, graphData: {}, sqlQuery: "" });
  // const [showVizModal, setShowVizModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);
  // const [isError, setIsError] = useState(false);
  const chatWindowRef = React.useRef(null);
  const mainContentBodyRef = React.useRef(null);
  const latestBotRef = React.useRef(null);

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999
  };

  const modalStyle = {
    background: "#fff",
    padding: "20px",
    borderRadius: "10px",
    width: "400px",
    maxWidth: "90%",
    boxSizing: "border-box"
  };


  const [feedbackMap, setFeedbackMap] = useState({});
  const [activePopupIndex, setActivePopupIndex] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState(""); // success or error message

  const handleFeedback = (index, type) => {
    setFeedbackMap((prev) => ({
      ...prev,
      [index]: type
    }));

    setActivePopupIndex(index);
  };

  useEffect(() => {
    setFeedbackMap({});
  }, []);
  // Scroll to bottom whenever chatHistory or response changes
  // useLayoutEffect(() => {
  //   if (mainContentBodyRef.current) {
  //     mainContentBodyRef.current.scrollTop = mainContentBodyRef.current.scrollHeight;
  //   }
  // }, [chatHistory, response]);

  // Load chat history per user and dashboard from localStorage
  useEffect(() => {
    if (!user || !activeDashboard) return;
    const key = `chatHistory_${user.id}_${activeDashboard}`;
    const stored = localStorage.getItem(key);
    let history = [];
    try {
      history = stored ? JSON.parse(stored) : [];
      // Sanitize all loaded messages
      history = history.map(msg => ({
        ...msg,
        text: escapeHTML(msg.text)
      }));
    } catch (e) {
      history = [];
    }
    setChatHistory(history);
  }, [user, activeDashboard]);


  useEffect(() => {
    setChatHistory([]);
    setResponse({ textData: "", tableData: {}, graphData: {}, sqlQuery: "" });
  }, [clearChatTrigger]);


  useLayoutEffect(() => {
    if (latestBotRef.current) {
      latestBotRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [response]);   // or [chatHistory] if you prefer

  const [hovered, setHovered] = useState(null);



  // Get first name from user object
  const userName = user?.name?.split(" ")[0] || "User";

  // API call for chat
  const handleQuerySend = async (query) => {
    setLoading(true);
    setResponse({ textData: "", tableData: {}, graphData: {}, sqlQuery: "" }); // Clear previous response so loader is at the bottom
    setChatHistory((prev) => {
      // Sanitize before storing
      const safeQuery = escapeHTML(query);
      const updated = [...prev, { sender: userName, text: safeQuery, type: "user" }];
      if (user && activeDashboard) {
        localStorage.setItem(`chatHistory_${user.id}_${activeDashboard}`, JSON.stringify(updated));
      }
      return updated;
    });
    setShowSuggestions(true);
    // setTimeout(() => {
    //   if (mainContentBodyRef.current) {
    //     mainContentBodyRef.current.scrollTop = mainContentBodyRef.current.scrollHeight;
    //   }
    // }, 0);

    let dashboardValue = activeDashboard;
    if (dashboardValue === "Firm and Opportunity") dashboardValue = "fno";
    if (dashboardValue === "Order Book") dashboardValue = "orderbook";

    const accessToken = user?.accessToken || sessionStorage.getItem("accessToken");

    try {
      const apiResponse = await chatApi({
        userQuery: query,
        dashboard: dashboardValue,
        accessToken
      });
      // setIsError(false);
      setLoading(false);
      setResponse({
        textData: apiResponse.textData || "",
        tableData: apiResponse.tableResponse || {},
        graphData: apiResponse.visualizationData || {},
        sqlQuery: apiResponse.sqlQuery || ""
      });
      setChatHistory((prev) => {
        // Sanitize before storing
        const safeText = escapeHTML(apiResponse.textData || "");
        const updated = [...prev, {
          sender: "Agent",
          text: safeText,
          type: "bot",
          responseData: {
            textData: safeText,
            tableData: apiResponse.tableResponse || {},
            graphData: apiResponse.visualizationData || {},
            sqlQuery: apiResponse.sqlQuery || ""
          }
        }];
        if (user && activeDashboard) {
          localStorage.setItem(`chatHistory_${user.id}_${activeDashboard}`, JSON.stringify(updated));
        }
        return updated;
      });
    } catch (error) {
      setLoading(false);
      let errorMsg = "Something went wrong, please try after some time.";
      if (error.response && error.response.data) {
        if (error.response.data.error_message) {
          errorMsg = error.response.data.error_message;
        }
      }
      // setIsError(true);
      setResponse({
        textData: errorMsg,
        tableData: {},
        graphData: {},
        sqlQuery: ""
      });
      setChatHistory((prev) => {
        // Sanitize before storing
        const safeText = escapeHTML(errorMsg);
        const updated = [...prev, {
          sender: "Agent",
          text: safeText,
          type: "bot",
          isError: true,
          responseData: {
            textData: safeText,
            tableData: {},
            graphData: {},
            sqlQuery: ""
          }
        }];
        if (user && activeDashboard) {
          localStorage.setItem(`chatHistory_${user.id}_${activeDashboard}`, JSON.stringify(updated));
        }
        return updated;
      });
    }
    // setTimeout(() => {
    //   if (mainContentBodyRef.current) {
    //     mainContentBodyRef.current.scrollTop = mainContentBodyRef.current.scrollHeight;
    //   }
    // }, 0);
  };

  useEffect(() => {
    // Clear response when dashboard changes, but keep chat history
    setResponse({ textData: "", tableData: {}, graphData: [] });
    setShowSuggestions(true);
    // Do not clear chat history here
  }, [activeDashboard]);

  // Suggestion click populates input and hides suggestions
  // Suggestion click populates input but keeps suggestions visible
  const handleSuggestionClick = (suggestion) => {
    // Do not hide suggestions
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("selectStaticSuggestion", { detail: suggestion }));
    }
  };

  const staticSuggestions = dashboardSuggestions[activeDashboard] || [];


  return (
    <div className="main-content-body" ref={mainContentBodyRef}>
      <div>
        <div className="dashboard-name">{activeDashboard}</div>
        {showSuggestions && staticSuggestions.length > 0 && (
          <div className="suggestions">
            <strong style={{ display: "block", marginBottom: 4 }}>Suggestions:</strong>
            {staticSuggestions.map((s, idx) => (
              <button key={idx} onClick={() => handleSuggestionClick(s)}>{s}</button>
            ))}
          </div>
        )}</div>
      <div className="chat-window" >
        {/* All content scrolls with the page, no custom scroll bar */}
        <div ref={chatWindowRef} style={{ flex: 1, overflowY: "auto", paddingBottom: 100 }}>

          {/* Chat history */}
          <div style={{ marginBottom: 16 }}>
            {chatHistory.map((msg, idx) => {
              const isLastMessage = idx === chatHistory.length - 1;
              const isBot = msg.type === "bot";

              return (
                <div
                  key={idx}
                  ref={isLastMessage && isBot ? latestBotRef : null}
                  style={{
                    flexDirection: msg.type === "user" ? "row-reverse" : "row",
                    alignItems: "center",
                    marginBottom: 6
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #eee",
                      padding: "8px 14px",
                      maxWidth: "90%",
                      fontSize: 15,
                      marginBottom: msg.type === "user" ? 12 : 0
                    }}
                  >
                    <strong style={{ fontSize: 13, color: msg.type === "user" ? "#005A9E" : "#007bff" }}>
                      {msg.sender}
                    </strong>

                    <div style={{ marginTop: 2 }} className={msg.isError ? "error-response-text" : ""}>
                      {msg.type === "bot" ? <ReactMarkdown>{msg.text}</ReactMarkdown> : msg.text}
                    </div>
                  {/* Disclaimer: only for bot messages */}
                  {msg.type === 'bot' && (
                    <div
                      style={{
                        marginBottom: 10,
                        fontSize: '11px',
                        color: '#888',
                        fontStyle: 'italic',
                        fontFamily: 'Segoe UI, Arial, sans-serif',
                        letterSpacing: '0.01em',
                        fontWeight: 400
                      }}
                    >
                      Disclaimer: This is an AI-generated response and may not be fully accurate. Please verify important details.
                    </div>
                  )}
                  </div>
                  {/* ✅ Feedback Buttons */}
                  {msg.type === "bot" && (
                  
                    <div style={{ display: "flex", justifyContent: "flex-start", gap: 12 }}>
                      <button
                        className="icon_btn like_btn"
                        onMouseEnter={() => setHovered(`like-${idx}`)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => handleFeedback(idx, "like")}
                      >
                        {(feedbackMap[idx] === "like" || hovered === `like-${idx}`) ? (
                          <AiFillLike color="#4f5bd5" />
                        ) : (
                          <AiOutlineLike color="black" />
                        )}
                      </button>

                      <button
                        className="icon_btn dislike_btn"
                        onMouseEnter={() => setHovered(`dislike-${idx}`)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => handleFeedback(idx, "dislike")}
                      >
                        {(feedbackMap[idx] === "dislike" || hovered === `dislike-${idx}`) ? (
                          <AiFillDislike color="#2222e4" />
                        ) : (
                          <AiOutlineDislike color="black" />
                        )}
                      </button>
                    </div>
                  )}
                </div>

              );
            })}
          </div>
          {/* Loader while waiting for response */}
          {
            loading && (
              <div className="dotted-loader">
                <span>.</span><span>.</span><span>.</span>
              </div>
            )
          }
          {/* Show new visualization modal for latest response only, after loader */}
          {
            !loading && (
              (
                // (response.sqlQuery) ||
                (
                  response.tableData &&
                  Array.isArray(response.tableData.headers) &&
                  response.tableData.headers.length > 0 &&
                  Array.isArray(response.tableData.rows) &&
                  response.tableData.rows.length > 0
                )
                // ||
                // (
                //   response.graphData &&
                //   (Array.isArray(response.graphData)
                //     ? response.graphData.length > 0
                //     : Object.keys(response.graphData).length > 0)
                // )
              ) && (
                <div style={{ marginTop: 24 }}>
                  <ResponseTabs
                    sqlQuery={response.sqlQuery}
                    tableData={
                      response.tableData &&
                        Array.isArray(response.tableData.headers) &&
                        response.tableData.headers.length > 0 &&
                        Array.isArray(response.tableData.rows) &&
                        response.tableData.rows.length > 0
                        ? {
                          headers: response.tableData.headers,
                          rows: response.tableData.rows
                        }
                        : {}
                    }
                    graphData={response.graphData}
                  />
                </div>
              )
            )
          }

        </div>
        {/* Sticky chat input at bottom */}
        <div className="boxWrp">
          <ChatBox
            selectedQuery={selectedQuery}
            onQuerySend={handleQuerySend}
            activeDashboard={activeDashboard}
            user={user}
          />
        </div>
      </div>

      {activePopupIndex !== null && (
        <div
          style={overlayStyle}
          onClick={() => setActivePopupIndex(null)} // 👈 close on outside click
        >
          <div
            style={modalStyle}
            onClick={(e) => e.stopPropagation()} // 👈 prevent closing when clicking inside
          >
            <h3 style={{ marginBottom: 10 }}>
              {feedbackMap[activePopupIndex] === "like"
                ? "What did you like?"
                : "What went wrong?"}
            </h3>

            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Write your feedback..."
              style={{
                display: "block",
                width: "100%",
                minHeight: "80px",
                padding: "8px",
                marginBottom: "12px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
                resize: "vertical"
              }}
            />

            <div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  onClick={() => {
                    setActivePopupIndex(null);
                    setFeedbackText("");
                    setFeedbackStatus("");
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: feedbackLoading ? "not-allowed" : "pointer"
                  }}
                  disabled={feedbackLoading}
                >
                  Cancel
                </button>

                <button
                  onClick={async () => {
                    setFeedbackLoading(true);
                    setFeedbackStatus("");
                    try {

                      // Find the latest user message for user_query
                      const userMsg = chatHistory.slice(0, activePopupIndex + 1).reverse().find(m => m.type === "user");
                      // Find the latest bot response for sql_query
                      const lastBotMsg = chatHistory.slice(0, activePopupIndex + 1).reverse().find(m => m.type === "bot");
                      const sql_query = lastBotMsg?.responseData?.sqlQuery || "";
                      const dashboardValue = activeDashboard === "Firm and Opportunity" ? "fno" : (activeDashboard === "Order Book" ? "orderbook" : activeDashboard);
                      const accessToken = user?.accessToken || sessionStorage.getItem("accessToken");
                      await sendFeedback({
                        user_email: user?.email || "",
                        is_like: feedbackMap[activePopupIndex] === "like" ? 1 : 0,
                        user_feedback_message: feedbackText,
                        user_query: userMsg?.text || "",
                        dashboard: dashboardValue,
                        sql_query,
                        accessToken
                      });
                      setFeedbackStatus("Feedback recorded. Thank you!");
                      setTimeout(() => {
                        setActivePopupIndex(null);
                        setFeedbackText("");
                        setFeedbackStatus("");
                      }, 1200);
                    } catch (err) {
                      setFeedbackStatus(err.message || "Failed to submit feedback");
                    } finally {
                      setFeedbackLoading(false);
                    }
                  }}
                  style={{
                    background: feedbackLoading ? "#aaa" : "#007bff",
                    color: "#fff",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: feedbackLoading ? "not-allowed" : "pointer"
                  }}
                  disabled={feedbackLoading || !feedbackText.trim()}
                >
                  {feedbackLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
              {feedbackStatus && (
                <div style={{ marginTop: 10, color: feedbackStatus.includes("Thank you") ? "green" : "red" }}>
                  {feedbackStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

  );

}

export default MainContent;


// Helper to transform tableData to graphData for ResponseTabs
// function getGraphDataFromTable(tableData) {
//   if (!tableData || !tableData.headers || !tableData.rows || tableData.rows.length === 0) return null;
//   // For bar chart, use first column as labels, each other column as a series
//   // We'll just use the first y column for now for compatibility with ResponseTabs' BarChart
//   const labels = tableData.rows.map(row => row[0]);
//   const values = tableData.headers.length > 1 ? tableData.rows.map(row => {
//     // If value is numeric, use as is, else try parseFloat
//     const v = row[1];
//     return typeof v === 'number' ? v : parseFloat(v) || 0;
//   }) : [];
//   return {
//     type: 'barChart',
//     labels,
//     values
//   };
// }
